import {InfiniteData} from '@tanstack/react-query';
import {parse} from 'qs';
import {useCallback, useEffect} from 'react';
import {getIsAgeRestricted} from '../../../../common/getIsAgeRestricted';
import {t} from '../../../../common/getTranslations';
import {omitNullUndefined} from '../../../../common/omitNullUndefined';
import {requestContext} from '../../../../common/requestContext';
import {selectListAndRestData} from '../../../../common/selectListAndRestData';
import {pushToRouter} from '../../../../common/shared/utils/pushToRouter';
import {twMerge} from '../../../../common/twMerge';
import useTypedDispatch from '../../../../common/useTypedDispatch';
import {ButtonTab} from '../../../../design-system/atoms/ButtonTab/ButtonTab';
import {Grid} from '../../../../design-system/atoms/Grid/Grid';
import {Loading} from '../../../../design-system/atoms/Loading/Loading';
import {TabPositionControl} from '../../../../design-system/atoms/TabPositionControl/TabPositionControl';
import {Text} from '../../../../design-system/atoms/Text/Text';
import {NormalListViewItem} from '../../../../design-system/organisms/NormalListViewItem/NormalListViewItem';
import {SearchThemeKeywordToolbar} from '../../../../design-system/organisms/SearchThemeKeywordToolbar/SearchThemeKeywordToolbar';
import {useDialog} from '../../../../dialog/useDialog.hook';
import {
  SearchThemeKeywordInput,
  SearchThemeKeywordQuery,
} from '../../../../graphql/generated/types';
import {setHeader} from '../../../../header/HeaderSlice';
import {LOG_CLICK_EVENT} from '../../../../logging/logEvent';
import {putTiaraObjectProps} from '../../../../logging/putTiaraObjectProps';
import {TrackEventObject} from '../../../../logging/TrackEventObject.interface';
import {useLogin} from '../../../../user/useLogin.hook';
import {useInfiniteSearchThemeKeywordQuery} from '../../../../wrapper/searchWrapper';

interface SearchThemeKeywordContainerProps {
  searchThemeKeywordInput: SearchThemeKeywordInput;
  data: InfiniteData<SearchThemeKeywordQuery>;
}

export function SearchThemeKeywordContainer({
  searchThemeKeywordInput,
  data: searchThemeKeywordQuery,
}: SearchThemeKeywordContainerProps) {
  const data = selectListAndRestData<
    SearchThemeKeywordQuery,
    'searchThemeKeyword'
  >('searchThemeKeyword', searchThemeKeywordQuery);

  const {user} = useLogin();
  const {isFetching, isFetchingNextPage, isPlaceholderData} =
    useInfiniteSearchThemeKeywordQuery(
      {
        input: searchThemeKeywordInput,
      },
      {
        initialPageParam: {input: searchThemeKeywordInput},
        getNextPageParam: () => undefined,
      }
    );

  const {DialogWrapper, Dialog, showDialog, hideDialog} = useDialog({
    isMobile: requestContext.requestInfo.display.isMobile,
  });

  const handleSelectSortOption = useCallback(
    (sortType: string) => {
      hideDialog();
      pushToRouter({
        ...parse(location.search, {ignoreQueryPrefix: true}),
        sortType,
      });
    },
    [hideDialog]
  );

  const {isMobile} = requestContext.requestInfo.display;
  const dispatch = useTypedDispatch();

  useEffect(() => {
    if (data) {
      dispatch(
        setHeader({
          customTiaraObject: {
            click: {
              layer1: '상단',
            },
            customProps: {
              page_keyword: searchThemeKeywordInput.filterList[0],
              page_sort: data.selectedSortOption.name,
            },
          },
        })
      );
    }
  }, [data, dispatch, searchThemeKeywordInput]);

  return (
    <>
      {data && (
        <div
          className={twMerge(
            isMobile && 'mb-15pxr',
            !isMobile && 'mb-84pxr',
            (data.total === 0 || isFetching) && 'flex flex-1 flex-col'
          )}
          {...putTiaraObjectProps({
            customProps: {
              page_keyword: data.filterList[0],
              page_sort: data.selectedSortOption.name,
              content_cnt: String(data.total),
            },
          })}
        >
          {!isMobile && (
            <div className="mt-20pxr flex h-54pxr shrink-0 items-center px-15pxr">
              {data.filterList.map((filter, index) => (
                <Text
                  className="font-large3-bold text-theme-solid-100"
                  key={filter}
                >
                  {index > 0 && ', '}#{filter}
                </Text>
              ))}
            </div>
          )}
          {isMobile && (
            <TabPositionControl
              depth={1}
              isMobile={true}
              isWebView={requestContext.requestInfo.display.isWebView}
            >
              <div className="mx-15pxr flex h-44pxr items-center bg-theme-bg-base">
                {data.filterList.map(value => (
                  <ButtonTab
                    key={value}
                    className="h-30pxr border-theme-solid-100 px-14pxr"
                  >
                    #{value}
                  </ButtonTab>
                ))}
              </div>
            </TabPositionControl>
          )}
          <SearchThemeKeywordToolbar
            className={twMerge(
              'h-44pxr shrink-0 px-15pxr',
              !isMobile && 'mt-32pxr'
            )}
            total={data.total}
            sortOptionName={data.selectedSortOption.name}
            onClickSortOption={showDialog}
          />
          <DialogWrapper
            {...putTiaraObjectProps({
              click: {
                layer1: '정렬옵션',
              },
            })}
          >
            <Dialog.Dimmed />
            <Dialog.List className="space-y-0">
              {data.sortOptionList.map(option => (
                <Dialog.Option
                  key={option.id}
                  onClick={e =>
                    LOG_CLICK_EVENT('정렬_클릭', e, () =>
                      handleSelectSortOption(option.param)
                    )
                  }
                  isChecked={option.param === data.selectedSortOption.param}
                  {...putTiaraObjectProps({
                    click: {
                      copy: option.name,
                    },
                    customProps: {
                      page_keyword: data.filterList[0],
                      page_sort: data.selectedSortOption.name,
                    },
                  })}
                >
                  {option.name}
                </Dialog.Option>
              ))}
            </Dialog.List>
            <Dialog.BottomCombination>
              <Dialog.Button onClick={hideDialog}>취소</Dialog.Button>
            </Dialog.BottomCombination>
          </DialogWrapper>
          {!isFetching && data.total === 0 && (
            <div className="font-medium1 text-theme-solid-100 flex-center-full">
              {t('search_result_empty')}
            </div>
          )}
          {isFetching && !isFetchingNextPage && (
            <div className="flex-center-full">
              <Loading />
            </div>
          )}
          {!isPlaceholderData && (
            <Grid
              cols={isMobile ? 1 : 2}
              tabletCols={2}
              gapPx={0}
              className="my-5pxr"
            >
              {data.list.map(({eventLog, ...item}, index) => {
                const omitEventLog = omitNullUndefined(
                  eventLog
                ) as TrackEventObject;
                return (
                  <NormalListViewItem
                    key={item.id}
                    item={item}
                    isAgeRestricted={getIsAgeRestricted({
                      age: user?.age,
                      authenticatedAt: user?.authenticatedAt,
                      ageGrade: item.ageGrade,
                      selfCensorship: item.selfCensorship,
                    })}
                    onClick={e => LOG_CLICK_EVENT('작품_클릭', e)}
                    {...putTiaraObjectProps({
                      click: {
                        layer1: '검색결과목록',
                        ordnum: String(index + 1),
                      },
                      ...(omitEventLog &&
                        omitEventLog.eventMeta && {
                          eventMeta: omitEventLog.eventMeta,
                        }),
                    })}
                  />
                );
              })}
            </Grid>
          )}
        </div>
      )}
    </>
  );
}
